import React from "react";
import { useIntl } from "react-intl";
import { CloudMoon, PauseCircle, UserCheck } from "lucide-react";
import { Switch } from "components/ui/switch";
import { cn } from "lib/utils";

type ProviderStandbyBannerProps = {
    isOnSale: boolean;
    disabled?: boolean;
    onIsOnSaleChange: (isOnSale: boolean) => void;
};

const ProviderStandbyBanner: React.FC<ProviderStandbyBannerProps> = ({
    isOnSale,
    disabled = false,
    onIsOnSaleChange,
}) => {
    const intl = useIntl();

    return (
        <div className="mb-6 flex flex-col gap-6 rounded-2xl bg-slate-800 px-5 py-5 text-white sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div className="flex min-w-0 items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-slate-700/70">
                    <CloudMoon className="h-6 w-6 text-amber-300" aria-hidden />
                </div>
                <div className="min-w-0">
                    <p className="text-base font-semibold leading-6">
                        {intl.formatMessage({ id: "profile.my_services.standby.title" })}
                    </p>
                    <p className="mt-1 text-sm leading-5 text-slate-300">
                        {intl.formatMessage({ id: "profile.my_services.standby.subtitle" })}
                    </p>
                </div>
            </div>

            <div className="flex shrink-0 items-center justify-end gap-4 sm:justify-center">
                <div
                    className={cn(
                        "flex w-16 flex-col items-center gap-1 text-center text-xs font-medium",
                        !isOnSale ? "text-white" : "text-slate-400",
                    )}
                >
                    <PauseCircle className="h-5 w-5" aria-hidden />
                    <span>{intl.formatMessage({ id: "profile.my_services.standby.paused" })}</span>
                </div>

                <Switch
                    checked={isOnSale}
                    disabled={disabled}
                    onCheckedChange={onIsOnSaleChange}
                    className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-slate-600"
                    aria-label={intl.formatMessage({ id: "profile.my_services.standby.toggle_label" })}
                />

                <div
                    className={cn(
                        "flex w-16 flex-col items-center gap-1 text-center text-xs font-medium",
                        isOnSale ? "text-white" : "text-slate-400",
                    )}
                >
                    <UserCheck className="h-5 w-5" aria-hidden />
                    <span>{intl.formatMessage({ id: "profile.my_services.standby.active" })}</span>
                </div>
            </div>
        </div>
    );
};

export default ProviderStandbyBanner;
